import { createContext, useContext, useState, useCallback, ReactNode, RefObject, useEffect } from "react";
import { FlyingCoins } from "@/components/animations/FlyingCoins";

interface CoinAnimation {
  id: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  count: number;
}

interface CoinAnimationContextType {
  triggerCoinAnimation: (sourceElement: HTMLElement, credits: number) => void;
  registerDestination: (ref: RefObject<HTMLElement>) => void;
}

const CoinAnimationContext = createContext<CoinAnimationContextType | undefined>(undefined);

export function CoinAnimationProvider({ children }: { children: ReactNode }) {
  const [animations, setAnimations] = useState<CoinAnimation[]>([]);
  const [destinationRef, setDestinationRef] = useState<RefObject<HTMLElement> | null>(null);

  // Create portal root on mount
  useEffect(() => {
    let portalRoot = document.getElementById('coin-portal-root');
    if (!portalRoot) {
      portalRoot = document.createElement('div');
      portalRoot.id = 'coin-portal-root';
      portalRoot.style.cssText = 'position: fixed; inset: 0; pointer-events: none; z-index: 99999;';
      document.body.appendChild(portalRoot);
    }
    return () => {
      // Cleanup on unmount
      const root = document.getElementById('coin-portal-root');
      if (root && root.childNodes.length === 0) {
        root.remove();
      }
    };
  }, []);

  const registerDestination = useCallback((ref: RefObject<HTMLElement>) => {
    console.log('📍 Destination registered:', ref.current);
    setDestinationRef(ref);
  }, []);

  const triggerCoinAnimation = useCallback((sourceElement: HTMLElement, credits: number) => {
    console.log('🪙 triggerCoinAnimation called:', { 
      hasDestination: !!destinationRef?.current,
      credits,
      sourceElement 
    });
    
    if (!destinationRef?.current) {
      console.warn('⚠️ No destination registered for coin animation');
      return;
    }

    const sourceRect = sourceElement.getBoundingClientRect();
    const destRect = destinationRef.current.getBoundingClientRect();

    const startX = sourceRect.left + sourceRect.width / 2;
    const startY = sourceRect.top + sourceRect.height / 2;
    const endX = destRect.left + destRect.width / 2;
    const endY = destRect.top + destRect.height / 2;

    // Calculate number of coins (1 coin per 0.1 credit, max 8)
    const coinCount = Math.min(Math.ceil(credits / 0.1), 8);

    console.log('🪙 Creating coin animation:', {
      startX,
      startY,
      endX,
      endY,
      coinCount
    });

    const newAnimation: CoinAnimation = {
      id: crypto.randomUUID(),
      startX,
      startY,
      endX,
      endY,
      count: coinCount,
    };

    setAnimations((prev) => [...prev, newAnimation]);
  }, [destinationRef]);

  const handleAnimationComplete = useCallback((id: string) => {
    setAnimations((prev) => prev.filter((a) => a.id !== id));
  }, []);

  return (
    <CoinAnimationContext.Provider value={{ triggerCoinAnimation, registerDestination }}>
      {children}
      <FlyingCoins animations={animations} onAnimationComplete={handleAnimationComplete} />
    </CoinAnimationContext.Provider>
  );
}

export function useCoinAnimation() {
  const context = useContext(CoinAnimationContext);
  if (!context) {
    throw new Error("useCoinAnimation must be used within CoinAnimationProvider");
  }
  return context;
}
